import React from "react";

const VisibilityVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson reframes visibility as making your impact easy to notice and easy to remember, not being loud. It introduces four practical modes to rotate through: inform with clear outcomes, educate with sharp signal, entertain by being a memorable human, and stay topical by linking your work to current themes. It also highlights the 80th floor problem: leaders see everyone as dots, so you need contrast, legible results, and multiple voices who can vouch for you.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>If you're wearing the <strong>invisibility cloak</strong> to work everyday, congratulations, you've mastered the art of being unnoticed.</p>

    <p>Invisibility might save wizards, not careers.</p>

    <p>In the corporate world, what isn't seen… doesn't grow. AKA you need to be <strong>visible</strong>.</p>

    <p>Visibility does not mean you have to scream to be heard. It's about taking responsibility for your work, making sure it earns the right space in people's minds.</p>

    <p>In simple words, it's about making your impact memorable, so the right stakeholders remember you.</p>

    <p>That's <strong>real visibility</strong>.</p>

    <p><strong>How do you build it?</strong></p>

    <p>There's four simple modes you can rotate through to become visible:</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">1. Inform: Make outcomes legible.</h3>
    <p>Be the recap engine. Send short updates after key meetings.</p>
    <p>Offer clarity and context, not drama or essays.</p>
    <p>When people stay oriented because of you, they trust you with bigger work.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">2. Educate: Curate signal, not noise.</h3>
    <p>Share 2–3 sharp insights, not long think pieces.</p>
    <p>For instance, a Friday "3×3 brief", three links, three implications, three questions.</p>
    <p>You become known as the person who expands perspective.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">3. Entertain: Be a memorable human.</h3>
    <p>People remember emotion and story.</p>
    <p>Add lightness when appropriate, a relatable analogy, a small win meme, a thoughtful connection.</p>
    <p>Visibility doesn't mean being robotic, it's about being remembered.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">4. Be Topical: Connect to what's hot now.</h3>
    <p>Engage with current company themes, a product launch, a cost-cutting initiative, a new strategy.</p>
    <p>Add substance to that conversation.</p>
    <p>Visibility rides on relevance.</p>

    <p>There's a concept I love: the "<strong>80th floor</strong>" problem. Imagine top leaders looking down from 80 floors up. From that height, everyone looks like dots. The difference between "good" and "great" performance becomes barely visible.</p>

    <p>Your job is to stand out even from the 80th floor, through</p>
    <ul style="margin-left: 20px;">
      <li>Clear, legible outcomes.</li>
      <li>Consistent patterns of reliability.</li>
      <li>And multiple credible voices who can vouch for you.</li>
    </ul>

    <p>The point is shouting louder does not help you, instead <strong>increase contrast</strong>.</p>
    <p>Make your results easier to see, your stories easier to retell, and your impact easier to advocate for.</p>

    <p>Stakeholders are the web that decides.</p>
    <p>Visibility is how your work travels through that web.</p>

    <p>Because your promotion doesn't start in the appraisal room… it starts long before that, in the everyday moments where your work becomes visible.</p>

    <p><strong>Which of these modes are you leveraging to become visible?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default VisibilityVideo;